<template>
  <div>
    <FormFieldLabel v-if="label" :for="`${name}_input`">
      {{ label }}
    </FormFieldLabel>
    <div class="relative rounded-md">
      <input
        :id="`${name}_input`"
        ref="inputRef"
        :disabled="disabled"
        type="text"
        :name="name"
        :class="[
          'block w-full rounded-md border border-background-fg/8 text-right text-2xl font-bold text-background-fg/70 focus:outline-none',
          hasError
            ? 'border-error text-error placeholder:text-error focus:border-error focus:ring-error'
            : 'focus:border-primary focus:ring-primary',
        ]"
        :placeholder="placeholder"
        :value="disabled ? '' : formattedValue"
        :aria-invalid="hasError"
        enterkeyhint="done"
        @blur="handleBlur"
        @keydown.backspace="handleBackspace"
        @keydown.enter="handleEnter"
      />
    </div>
    <template v-if="hasError">
      <p class="mt-1 text-xs font-medium text-error">
        {{ errorMessage }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useField } from "vee-validate"
import {
  useCurrencyInput,
  CurrencyDisplay,
  ValueScaling,
} from "vue-currency-input"

const props = withDefaults(
  defineProps<{
    name?: string
    label?: string
    placeholder?: string
    currency?: string
    disabled?: boolean
    min?: number
    max?: number
  }>(),
  {
    name: "",
    label: "",
    placeholder: "",
    currency: "GBP",
    disabled: false,
    min: 0,
    max: undefined,
  }
)

const {
  numberValue: internalValueNumber,
  inputRef,
  setValue: setInternalValue,
  formattedValue,
} = useCurrencyInput(
  {
    currency: props.currency,
    currencyDisplay: CurrencyDisplay.symbol,
    autoDecimalDigits: true,
    hideCurrencySymbolOnFocus: false,
    hideGroupingSeparatorOnFocus: false,
    hideNegligibleDecimalDigitsOnFocus: false,
    valueScaling: ValueScaling.precision,
    valueRange: {
      min: props.min,
      max: props.max,
    },
  },
  false
)

const {
  value: fieldValue,
  errorMessage,
  handleBlur,
  handleChange,
} = useField<number>(toRef(props, "name"))

watch(
  [fieldValue, inputRef],
  ([newFieldValue]) => {
    if (newFieldValue !== undefined) {
      setInternalValue(newFieldValue)
    }
  },
  { immediate: true }
)

const hasError = computed(() => !!errorMessage.value)

watch(internalValueNumber, (newValue) => {
  if (newValue === fieldValue.value) return
  if (newValue === null || newValue === undefined) {
    handleChange(0)
    return
  }

  handleChange(newValue)
})

const handleBackspace = (event: KeyboardEvent) => {
  if (internalValueNumber.value === 0) {
    event.preventDefault()
  }
}

const handleEnter = (event: Event) =>
  (event?.target as HTMLInputElement)?.blur()
</script>
